import type { ReactElement, ReactNode } from 'react';
import { Button } from './Button.tsx';
import { TeslynMark } from './TeslynMark.tsx';

export function Loading({ what }: { readonly what: string }): ReactElement {
  return (
    <p className="state body muted" role="status" aria-live="polite">
      Loading {what}…
    </p>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  readonly message: string;
  readonly onRetry?: () => void;
}): ReactElement {
  return (
    <div className="state state--error" role="alert">
      <span className="body">Could not load: {message}</span>
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/** Page title row with optional actions. */
export function PageHeader({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children?: ReactNode;
}): ReactElement {
  return (
    <header className="page__head">
      <div>
        <h1 className="title page__title">{title}</h1>
        {subtitle && <p className="body muted page__sub">{subtitle}</p>}
      </div>
      {children && <div className="row">{children}</div>}
    </header>
  );
}

/**
 * A tab's own header inside a page with tabs (People, Company, the Deleted list in Projects): its words and buttons
 * under the page's title and tabs. The title is for screen readers (the tab above already shows it).
 */
export function SectionHeader({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children?: ReactNode;
}): ReactElement {
  return (
    <header className="page__head">
      <div>
        <h2 className="sr-only">{title}</h2>
        {subtitle && <p className="body muted page__sub">{subtitle}</p>}
      </div>
      {children && <div className="row">{children}</div>}
    </header>
  );
}

/**
 * "Powered by Teslyn" credit (brand/white-label.md): caption, ink-muted, never removed. The single-ink Teslyn mark
 * sits before the words (brand/brand-book.md "White-label"), at the 16 px minimum size with half its height as gap.
 */
export function PoweredBy(): ReactElement {
  return (
    <span className="caption muted powered-by">
      <TeslynMark size={16} />
      <span>Powered by Teslyn</span>
    </span>
  );
}
